import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Input } from '../packages/ui';
import { getErrorMessage } from '../packages/api-client';

export const MIN_PASSWORD_LENGTH = 8;

interface Props {
    /** Called with the new password once it passes client-side validation.
     *  Throw or return a rejected promise to surface a toast error. */
    onSubmit: (password: string) => Promise<void>;
    /** Label for the submit button. Defaults to "Set password". */
    submitLabel?: string;
    /** Short hint rendered above the inputs. */
    helperText?: string;
}

/**
 * SetPasswordForm — two password fields with inline length/match validation
 * and a submit button. Shared between /reset-password and the org-invite
 * accept page so both flows share the same UX.
 */
export function SetPasswordForm({ onSubmit, submitLabel = 'Set password', helperText }: Props) {
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
    const mismatch = confirm.length > 0 && password !== confirm;
    const canSubmit = useMemo(
        () => password.length >= MIN_PASSWORD_LENGTH && password === confirm,
        [password, confirm],
    );

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!canSubmit) return;
        setIsLoading(true);
        try {
            await onSubmit(password);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            {helperText && <p className="login-subtitle">{helperText}</p>}
            <form className="login-form" onSubmit={handleSubmit}>
                <Input
                    id="new-password"
                    type="password"
                    label="New password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    error={tooShort ? `At least ${MIN_PASSWORD_LENGTH} characters.` : undefined}
                />
                <Input
                    id="confirm-password"
                    type="password"
                    label="Confirm new password"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                    error={mismatch ? 'Passwords do not match.' : undefined}
                />
                <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    isLoading={isLoading}
                    disabled={!canSubmit}
                    className="form-button"
                >
                    {submitLabel}
                </Button>
            </form>
        </>
    );
}
